import React, { useState, useRef, useEffect } from 'react';
import {MessageSquare, Edit, Check, X, Trash2} from 'lucide-react';
import { Conversation } from '../utils/types';

interface ConversationItemProps {
    conversation: Conversation;
    isActive: boolean;
    onClick: () => void;
    onEdit: (id: number, newTitle: string) => void;
    onDelete: (id: number) => void;
}

const ConversationItem: React.FC<ConversationItemProps> = ({
                                                               conversation,
                                                               isActive,
                                                               onClick,
                                                               onEdit,
                                                               onDelete
                                                           }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [newTitle, setNewTitle] = useState(conversation.title);
    const [showOptions, setShowOptions] = useState(false);
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const deleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const lastMessage = conversation.messages.length > 0
        ? conversation.messages[conversation.messages.length - 1].content
        : '';

    const handleEdit = () => {
        setIsEditing(true);
        setShowOptions(false);
    };

    useEffect(() => {
        return () => {
            if (deleteTimeoutRef.current) {
                clearTimeout(deleteTimeoutRef.current);
            }
        };
    }, []);

    const handleDeleteClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setConfirmingDelete(true);
        setShowOptions(false);
    };

    const handleConfirmDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        setConfirmingDelete(false);
        setIsDeleting(true);

        deleteTimeoutRef.current = setTimeout(() => {
            onDelete(conversation.id);
        }, 300);
    };

    const handleCancelDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        setConfirmingDelete(false);
    };

    const saveEdit = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (newTitle.trim() !== '') {
            onEdit(conversation.id, newTitle);
            setIsEditing(false);
        }
    };

    const cancelEdit = (e: React.MouseEvent) => {
        e.stopPropagation();
        setNewTitle(conversation.title);
        setIsEditing(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        e.stopPropagation();
        if (e.key === 'Enter') {
            if (newTitle.trim() !== '') {
                onEdit(conversation.id, newTitle);
                setIsEditing(false);
            }
        } else if (e.key === 'Escape') {
            setNewTitle(conversation.title);
            setIsEditing(false);
        }
    };

    const handleItemClick = () => {
        if (!isEditing) {
            onClick();
        }
    };

    const handleMouseEnter = () => {
        setShowOptions(true);
    };

    const handleMouseLeave = () => {
        setShowOptions(false);
    };

    return (
        <div
            className={`p-3 mb-2 rounded-lg cursor-pointer transition-all duration-300 ${
                isActive ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-100'
            } ${
                isDeleting ? 'opacity-0 scale-95 h-0 my-0 py-0 overflow-hidden' : 'opacity-100'
            }`}
            onClick={handleItemClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center flex-1 overflow-hidden">
                    <MessageSquare size={18} className="flex-shrink-0 mr-2" />
                    {isEditing ? (
                        <input
                            type="text"
                            value={newTitle}
                            onChange={(e) => setNewTitle(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={handleKeyDown}
                            className="w-full px-2 py-1 border border-blue-300 rounded focus:outline-none"
                            autoFocus
                        />
                    ) : (
                        <span className="truncate">{conversation.title}</span>
                    )}
                </div>

                {isEditing ? (
                    <div className="flex items-center ml-2">
                        <button
                            onClick={saveEdit}
                            className="p-1 text-green-500 hover:text-green-700 mr-1"
                            title="Save"
                        >
                            <Check size={16} />
                        </button>
                        <button
                            onClick={cancelEdit}
                            className="p-1 text-red-500 hover:text-red-700"
                            title="Cancel"
                        >
                            <X size={16} />
                        </button>
                    </div>
                ) : confirmingDelete ? (
                    <div className="flex items-center bg-white p-1 rounded shadow-sm">
                        <button
                            onClick={handleConfirmDelete}
                            className="p-1 text-xs font-medium text-white bg-red-500 rounded hover:bg-red-600 transition-colors mr-1"
                            title="Confirm delete"
                        >
                            Delete
                        </button>
                        <button
                            onClick={handleCancelDelete}
                            className="p-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                            title="Cancel"
                        >
                            Cancel
                        </button>
                    </div>
                ) : (
                    showOptions && (
                        <div className="flex items-center ml-2">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleEdit();
                                }}
                                className="p-1 text-gray-500 hover:text-blue-700 mr-1"
                                title="Edit"
                            >
                                <Edit size={16} />
                            </button>
                            <button
                                onClick={handleDeleteClick}
                                className="p-1 text-gray-500 hover:text-red-700 transition-all duration-200 transform hover:scale-110"
                                title="Delete"
                            >
                                <Trash2 size={16} />
                            </button>
                        </div>
                    )
                )}
            </div>
            {lastMessage && !isEditing && (
                <p className="text-sm text-gray-500 mt-1 truncate">
                    {lastMessage.substring(0, 50)}
                    {lastMessage.length > 50 ? '...' : ''}
                </p>
            )}
        </div>
    );
};

export default ConversationItem;